import { Toast, type ToastMessage } from 'primereact/toast';
import { useRef } from 'react';
import { ToastContext } from './ToastContext';

function calculateToastDuration(errorMessage: string) {
  const BASE_TIME_MS = 2000;
  const TIME_PER_CHAR_MS = 40;

  const totalDuration =
    BASE_TIME_MS + String(errorMessage).length * TIME_PER_CHAR_MS;

  const MAX_TIME_MS = 30000;

  return Math.min(totalDuration, MAX_TIME_MS);
}

export interface ToastContextInterface {
  showToast: (options: ToastMessage) => void;
  showSuccess: (message: string, summary?: string, life?: number) => void;
  showError: (message: string, summary?: string, life?: number) => void;
  showInfo: (message: string, summary?: string, life?: number) => void;
}

// wrap context provider to add functionality
export const ToastContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const toastRef = useRef<Toast>(null);

  const showToast = (options: ToastMessage) => {
    if (!toastRef.current) return;
    toastRef.current.show(options);
  };

  const showSuccess = (
    message: string,
    summary?: string,
    life: number = 5000,
  ) => {
    const _message =
      typeof message === 'string' ? message : JSON.stringify(message);
    toastRef.current?.show({
      severity: 'success',
      summary: summary || 'Success',
      detail: _message,
      life,
    });
  };

  const showError = (message: string, summary?: string, life?: number) => {
    const _message =
      typeof message === 'string' ? message : JSON.stringify(message);

    toastRef.current?.show({
      severity: 'error',
      summary: summary || 'Error',
      detail: _message,
      life: life || calculateToastDuration(_message),
    });
  };

  const showInfo = (message: string, summary?: string, life: number = 5000) => {
    const _message =
      typeof message === 'string' ? message : JSON.stringify(message);
    toastRef.current?.show({
      severity: 'info',
      summary: summary || 'Info',
      detail: _message,
      life,
    });
  };

  return (
    <ToastContext.Provider
      value={{ showToast, showSuccess, showError, showInfo }}
    >
      <Toast ref={toastRef} />
      <div>{children}</div>
    </ToastContext.Provider>
  );
};
